import { normalizeResult, readFileBytes } from '../../shared/index.js';

export const OFFICE_MAX_INPUT_BYTES = 50 * 1024 * 1024;

const MARKDOWN_API_BASE = String(import.meta.env?.VITE_MARKDOWN_API_BASE || '').replace(/\/+$/, '');

function apiUrl(path) {
  return `${MARKDOWN_API_BASE}${path}`;
}

function abortError() {
  return new DOMException('Conversion canceled.', 'AbortError');
}

function checkAborted(signal) {
  if (signal?.aborted) throw abortError();
}

async function responseJson(response) {
  const payload = await response.json().catch(() => null);
  if (response.ok) return payload;
  const message = payload?.error?.message || 'The document service could not complete the request.';
  const error = new Error(String(message));
  error.code = String(payload?.error?.code || 'REQUEST_FAILED');
  error.status = response.status;
  throw error;
}

function delay(ms, signal) {
  checkAborted(signal);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const cancel = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      reject(abortError());
    };
    signal?.addEventListener('abort', cancel, { once: true });
    if (signal?.aborted) cancel();
  });
}

function base64Bytes(encoded) {
  const value = String(encoded || '');
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function browserResult(payload) {
  if (!payload || typeof payload.markdown !== 'string') throw new Error('The document service returned an invalid result.');
  const result = {
    markdown: payload.markdown,
    warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
    assets: Array.isArray(payload.assets)
      ? payload.assets.map((asset) => ({
        path: asset.path,
        contentType: asset.contentType || 'application/octet-stream',
        blob: new Blob([base64Bytes(asset.blob)], { type: asset.contentType || 'application/octet-stream' }),
      }))
      : [],
  };
  return { ...normalizeResult(result), metadata: payload.metadata };
}

function taskUrl(suffix = '') {
  return apiUrl(`/api/markdown/task${suffix}`);
}

function taskHeaders(token, headers = {}) {
  return { ...headers, Authorization: `Bearer ${token}` };
}

async function cancelRemote(token) {
  if (!token) return;
  try {
    await fetch(taskUrl(), { method: 'DELETE', cache: 'no-store', keepalive: true, headers: taskHeaders(token) });
  } catch {
    // The local cancellation remains authoritative when the browser is offline.
  }
}

async function pollTask(token, signal, report) {
  let waitMs = 160;
  while (true) {
    checkAborted(signal);
    const response = await fetch(taskUrl(), { cache: 'no-store', headers: taskHeaders(token, { Accept: 'application/json' }), signal });
    const task = await responseJson(response);
    if (task.status === 'succeeded') return task;
    if (task.status === 'failed' || task.status === 'cancelled' || task.status === 'expired') {
      const error = new Error(task.error?.message || 'The document conversion failed.');
      error.code = task.error?.code || 'CONVERSION_FAILED';
      throw error;
    }
    report(task.status === 'queued' ? 'Waiting for the document service…' : 'Converting in the document service…', task.status === 'queued' ? 0.2 : 0.65);
    await delay(waitMs, signal);
    waitMs = Math.min(1000, Math.round(waitMs * 1.35));
  }
}

async function fetchResult(token, signal, mode) {
  checkAborted(signal);
  const response = await fetch(taskUrl('/result'), { cache: 'no-store', headers: taskHeaders(token, { Accept: 'application/json' }), signal });
  const payload = await responseJson(response);
  if (mode === 'inspect') {
    if (!Array.isArray(payload?.sheets)) throw new Error('The document service returned an invalid workbook inspection.');
    return { sheets: payload.sheets.map((sheet) => String(sheet)), warnings: Array.isArray(payload.warnings) ? payload.warnings : [] };
  }
  return browserResult(payload);
}

export function requireUploadConsent(consentElement) {
  if (consentElement?.checked === true) return true;
  const error = new Error('Confirm that you authorize this document to be uploaded for conversion before continuing.');
  error.code = 'UPLOAD_CONSENT_REQUIRED';
  throw error;
}

async function submit({ file, format, options = {}, mode = 'convert', signal, report = () => {}, consent = false }) {
  if (!consent) requireUploadConsent(null);
  const bytes = await readFileBytes(file, OFFICE_MAX_INPUT_BYTES, `${format.toUpperCase()} file`);
  checkAborted(signal);
  report('Uploading the document to the Kivoza conversion service…', 0.08);
  let token = '';
  try {
    const response = await fetch(apiUrl(`/api/markdown/${mode}`), {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Markdown-Format': format,
        'X-Markdown-File-Name': file.name || `document.${format}`,
        'X-Markdown-Options': JSON.stringify(options),
        Accept: 'application/json',
      },
      body: bytes,
      signal,
    });
    const accepted = await responseJson(response);
    token = String(accepted?.token || '');
    if (!token) throw new Error('The document service did not return a task token.');
    await pollTask(token, signal, report);
    report('Downloading the Markdown result…', 0.9);
    return await fetchResult(token, signal, mode);
  } catch (error) {
    if (signal?.aborted) {
      await cancelRemote(token);
      throw abortError();
    }
    throw error;
  }
}

export function convertOfficeFile(file, format, options, context) {
  return submit({ file, format, options, mode: 'convert', ...context });
}

export async function inspectXlsxFile(file, context) {
  return submit({ file, format: 'xlsx', options: {}, mode: 'inspect', ...context });
}

export function applyImageMode(result, mode = 'links') {
  if (mode === 'links') return result;
  const assets = new Map(result.assets.map((asset) => [asset.path, asset]));
  const markdown = result.markdown.replace(/!\[([^\]]*)\]\((assets\/[A-Za-z0-9._-]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g, (whole, alt, path) => {
    if (mode === 'alt') return alt || '';
    return '';
  });
  const referenced = new Set();
  if (mode === 'alt') {
    // The links have been replaced by alt text, so no binary is still needed.
  } else {
    for (const match of markdown.matchAll(/!?\[[^\]]*\]\((assets\/[A-Za-z0-9._-]+)/g)) referenced.add(match[1]);
  }
  return { ...result, markdown, assets: mode === 'alt' || mode === 'omit' ? [] : [...assets.values()].filter((asset) => referenced.has(asset.path)) };
}

export function removeFrontmatter(result) {
  return { ...result, markdown: result.markdown.replace(/^---\s*\n[\s\S]*?\n---\s*\n*/u, '').trim() };
}
